import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useNotification } from "../../../context/NotificationContext";
import expedientesService from "../../../services/expedientesService";
import { resumeJurisdiction } from "../../../services/common";
import { Card, CardActions, CardContent, CardHeader, Skeleton } from "@mui/material";
import MDBox from "components/MDBox";
import Grid from "@mui/material/Grid";
import DashboardLayout from "layouts/DashboardLayout";
import DashboardNavbar from "navbars/DashboardNavbar";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { DateFormat } from "services/common";

const EditFile = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  let { id } = useParams();
  id = Number(id);

  const dateNow = DateFormat(Date.now());
  const [numero, setNumero] = useState("");
  const [anio, setAnio] = useState("");
  const [cuaderno, setCuaderno] = useState("");
  const [caratula, setCaratula] = useState("");
  const [jurisdiccion, setJurisdiccion] = useState("");
  const [dependencia, setDependencia] = useState("");
  const [sorteo, setSorteo] = useState(dateNow);
  const [notificacion, setNotificacion] = useState(dateNow);
  const [aceptacion, setAceptacion] = useState(dateNow);
  const [linkExpediente, setLinkExpediente] = useState("");
  const [linkRepositorio, setLinkRepositorio] = useState("");
  const [jurisdiccionBrief, setJurisdiccionBrief] = useState("");
  const [honorario, setHonorario] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const hasBasePathData = jurisdiccionBrief && numero && anio;
  const basePath = hasBasePathData
    ? {
        title: `${jurisdiccionBrief} ${numero}/${anio}`,
        link: `/file/detail/${id}`,
      }
    : undefined;

  useEffect(() => {
    async function fetchExpedientes() {
      try {
        const data = await expedientesService.getExpedienteById(id);
        if (data.error) {
          showNotification(data.message, "error");
        } else {
          setNumero(data.numero);
          setAnio(data.anio);
          setCuaderno(data.cuaderno);
          setCaratula(data.caratula);
          setJurisdiccion(data.jurisdiccion);
          setDependencia(data.dependencia);
          setSorteo(data.fecha_sorteo);
          setAceptacion(data.fecha_aceptacion);
          setNotificacion(data.fecha_notificacion);
          setLinkExpediente(data.link_expediente);
          setLinkRepositorio(data.link_repositorio);
          setJurisdiccionBrief(resumeJurisdiction(data.jurisdiccion));
          setHonorario(data.honorario);
        }
      } catch {
        showNotification("Error al cargar el expediente", "error");
      } finally {
        setLoading(false);
      }
    }
    fetchExpedientes();
  }, [id]);

  const editExpediente = async () => {
    setSubmitting(true);
    try {
      const response = await expedientesService.editExpediente({
        id,
        numero,
        anio,
        caratula,
        cuaderno,
        jurisdiccion,
        dependencia,
        fecha_sorteo: sorteo,
        fecha_notificacion: notificacion,
        fecha_aceptacion: aceptacion,
        link_expediente: linkExpediente,
        link_repositorio: linkRepositorio,
        honorario,
      });
      if (response.success) {
        showNotification("Registro actualizado correctamente", "success");
      } else {
        showNotification(response.message, "error");
      }
    } catch {
      showNotification("Error al guardar el expediente", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteExpediente = async () => {
    const shouldDelete = window.confirm("¿Realmente desea eliminar este expediente?");
    if (!shouldDelete) return;
    setSubmitting(true);
    try {
      const response = await expedientesService.deleteExpediente(id);
      if (response.success) {
        showNotification("Registro eliminado correctamente", "success");
        navigate("/file/files");
      } else {
        showNotification(response.message, "error");
      }
    } catch {
      showNotification("Error al eliminar el expediente", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const SkeletonField = () => <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />;

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar title="Editar expediente" />
        <Card>
          <CardHeader title={<Skeleton width={180} />} />
          <CardContent>
            <MDBox pb={2} />
            <Grid container spacing={2}>
              {[4, 4, 4].map((xs, i) => (
                <Grid key={i} item xs={xs}><SkeletonField /></Grid>
              ))}
              {[12, 12, 12].map((xs, i) => (
                <Grid key={i} item xs={xs}><SkeletonField /></Grid>
              ))}
              {[4, 4, 4].map((xs, i) => (
                <Grid key={i} item xs={xs}><SkeletonField /></Grid>
              ))}
              {[12, 12, 4].map((xs, i) => (
                <Grid key={i} item xs={xs}><SkeletonField /></Grid>
              ))}
            </Grid>
          </CardContent>
          <CardActions>
            <Skeleton variant="rectangular" width={90} height={36} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" width={90} height={36} sx={{ borderRadius: 1 }} />
          </CardActions>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar title="Editar expediente" basePath={basePath} />
      <Card>
        <CardHeader title="Editar expediente" />
        <CardContent>
          <MDBox pb={2} />
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <MDInput
                fullWidth
                type="number"
                label="Número"
                value={numero || ""}
                onChange={(event) => setNumero(event.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <MDInput
                fullWidth
                type="number"
                label="Año"
                value={anio || ""}
                onChange={(event) => setAnio(event.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <MDInput
                fullWidth
                type="number"
                label="Cuaderno"
                value={cuaderno || ""}
                onChange={(event) => setCuaderno(event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                fullWidth
                type="text"
                label="Carátula"
                value={caratula || ""}
                onChange={(event) => setCaratula(event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                fullWidth
                type="text"
                label="Jurisdicción"
                value={jurisdiccion || ""}
                onChange={(event) => setJurisdiccion(event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                fullWidth
                type="text"
                label="Dependencia"
                value={dependencia || ""}
                onChange={(event) => setDependencia(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MDInput
                fullWidth
                type="date"
                label="Fecha sorteo"
                value={sorteo || ""}
                onChange={(event) => setSorteo(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MDInput
                fullWidth
                type="date"
                label="Fecha notificación"
                value={notificacion || ""}
                onChange={(event) => setNotificacion(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MDInput
                fullWidth
                type="date"
                label="Fecha aceptación"
                value={aceptacion || ""}
                onChange={(event) => setAceptacion(event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                fullWidth
                type="text"
                label="Link a expediente del juzgado"
                value={linkExpediente || ""}
                onChange={(event) => setLinkExpediente(event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                fullWidth
                type="text"
                label="Link a respositorio de archivos"
                value={linkRepositorio || ""}
                onChange={(event) => setLinkRepositorio(event.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <MDInput
                fullWidth
                type="number"
                step="0.01"
                label="Honorario"
                value={honorario || ""}
                onChange={(event) => setHonorario(event.target.value)}
              />
            </Grid>
          </Grid>
        </CardContent>
        <CardActions>
          <MDButton variant="gradient" color="success" onClick={editExpediente} disabled={submitting}>
            Guardar
          </MDButton>
          <MDButton variant="gradient" color="error" onClick={deleteExpediente} disabled={submitting}>
            Eliminar
          </MDButton>
        </CardActions>
      </Card>
    </DashboardLayout>
  );
};

export default EditFile;
